import React, { useEffect, useRef, useState } from 'react';
import { Modal, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { Text } from './Texte';
import { SafeAreaView } from 'react-native-safe-area-context';
import { LinearGradient } from 'expo-linear-gradient';
import { Ionicons } from './icons';
import { Button } from './Button';
import { GradientButton } from './GradientButton';
import { GradientText } from './GradientText';
import { DecorBlob } from './DecorBlob';
import {
  individualPlans,
  achatPossible,
  SCHOOL_CONTACT_EMAIL,
  schoolPlan,
  recommendedPlanFor,
  type PlanCard as PlanCardData,
} from '../config/plans';
import {
  BILLING_AVAILABLE,
  fetchTarifs,
  purchasePlan,
  purchaseProAnnual,
  restorePurchases,
} from '../services/purchases';
import { AnnualOfferCard } from './AnnualOfferCard';
import type { Tarifs } from '../services/purchases';
import type { QuotaRefusal } from '../services/claude';
import { prochaineRemiseAZero } from '../utils/quotaRefusal';
import type { PurchasablePlan } from '../config/purchases';
import { colors, gradients, radius, screenPadding, shadow, spacing, typography } from '../theme/tokens';
import type { FeatureKey, Plan } from '../types';
import { horsFrancais, t, tBrut } from '../i18n';
import { TermsScreen } from '../screens/TermsScreen';
import { PrivacyScreen } from '../screens/PrivacyScreen';
import { ouvrirLien } from '../utils/ouvrirLien';
import { CONFIRMATION_PAR_DEFAUT, confirmerPlan, type ReglagesConfirmation } from '../utils/confirmerPlan';

// Noms COMMERCIAUX : ils figurent tels quels sur les fiches App Store et les factures, et ne se
// traduisent donc pas. Un test le retient (config/__tests__/untranslated).
const PLAN_LABEL: Record<Plan, string> = { free: 'Free', plus: 'Accélérateur', pro: 'Pro', school: 'École' };

interface PaywallProps {
  currentPlan: Plan;
  feature: FeatureKey | null;
  /**
   * Raison du refus qui a ouvert le Paywall. `null` = ouverture délibérée : rien à expliquer, donc
   * aucun bandeau. C'est ce qui évite d'annoncer un refus à quelqu'un qui vient simplement regarder
   * les offres.
   */
  reason?: QuotaRefusal | null;
  onClose: () => void;
  /**
   * Succès achat/restauration → le parent relit le plan auprès du SERVEUR et le rend. Le paywall
   * n'annonce « activé » que si ce plan atteint celui acheté ; sans ce retour, il reste neutre.
   */
  onPurchased?: () => Promise<Plan>;
  confirmation?: ReglagesConfirmation; // réglable pour les tests ; défaut ~8 s
  testID?: string;
}

type Feedback = { tone: 'success' | 'info' | 'error'; text: string } | null;

type Tab = 'individuel' | 'ecoles';

// Liste de features avec coche charte (violet) alignée en tête de ligne.
function FeatureList({ features }: { features: string[] }) {
  return (
    <View style={styles.features}>
      {features.map((f) => (
        <View key={f} style={styles.featureRow}>
          <Ionicons name="checkmark" size={16} color={colors.violet} style={styles.check} />
          <Text style={styles.featureText}>{f}</Text>
        </View>
      ))}
    </View>
  );
}

export function Paywall({
  currentPlan,
  feature,
  reason = null,
  onClose,
  onPurchased,
  confirmation = CONFIRMATION_PAR_DEFAUT,
  testID,
}: PaywallProps) {
  const [tab, setTab] = useState<Tab>('individuel');
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<Feedback>(null);
  const monte = useRef(true);
  useEffect(
    () => () => {
      monte.current = false;
    },
    [],
  );
  // CGU ou confidentialité ouvertes par-dessus le paywall (règle 3.1.2), sans le fermer.
  const [docJuridique, setDocJuridique] = useState<'cgu' | 'confidentialite' | null>(null);
  const recommended = recommendedPlanFor(feature);
  // Carte mise en avant. Une seule à la fois ; départ sur le plan recommandé sinon Pro (grille 1.1.0).
  const [selected, setSelected] = useState<Plan>(recommended ?? 'pro');

  /**
   * Offre annuelle. `null` tant qu'on ne l'a pas, ET `null` si elle n'existe pas — distinguer les
   * deux n'aurait servi qu'à afficher une case vide. Package absent d'un ancien offering, réseau
   * coupé, achats indisponibles : le paywall reste exactement celui d'aujourd'hui.
   */
  const [tarifs, setTarifs] = useState<Tarifs>({ annuelle: null, mensuels: { plus: null, pro: null } });
  useEffect(() => {
    let vivant = true;
    void fetchTarifs().then((lus) => {
      if (vivant) setTarifs(lus);
    });
    return () => {
      vivant = false;
    };
  }, []);

  /**
   * Un achat réussi côté SDK n'est pas un plan ouvert côté serveur : le webhook RevenueCat peut
   * tarder. On relit donc le plan serveur (borné) avant d'annoncer quoi que ce soit ; si le serveur
   * ne le reconnaît pas encore, message neutre — jamais la promesse d'un plan qu'il refusera.
   */
  const confirmerSuccess = async (attendu: Plan, textes: { confirme: string; neutre: string }) => {
    setFeedback({ tone: 'info', text: t('paywall.activating') });
    const confirme = onPurchased
      ? await confirmerPlan(onPurchased, attendu, confirmation)
      : false;
    if (!monte.current) return;
    setFeedback(
      confirme ? { tone: 'success', text: textes.confirme } : { tone: 'info', text: textes.neutre },
    );
  };

  // Mappe le résultat d'achat/restauration en message clair (jamais de crash).
  const applyOutcome = async (
    outcome: { status: string; message?: string },
    attendu: Plan,
    textes: { confirme: string; neutre: string },
  ) => {
    if (outcome.status === 'success') {
      await confirmerSuccess(attendu, textes);
    } else if (outcome.status === 'pending') {
      setFeedback({ tone: 'info', text: t('paywall.pendingApproval') });
    } else if (outcome.status === 'error') {
      setFeedback({ tone: 'error', text: outcome.message ?? t('paywall.genericError') });
    }
    // 'cancelled' → aucun message (retour silencieux).
  };

  const subscribe = async (plan: Plan) => {
    if (busy || (plan !== 'plus' && plan !== 'pro')) return;
    setBusy(true);
    setFeedback(null);
    try {
      await applyOutcome(await purchasePlan(plan as PurchasablePlan), plan, {
        confirme: t('paywall.activated'),
        neutre: t('paywall.activationEnCours'),
      });
    } finally {
      if (monte.current) setBusy(false);
    }
  };

  // MÊME CHEMIN que le mensuel : mêmes états, même message de succès, même barre `busy`.
  const subscribeAnnual = async () => {
    if (busy) return;
    setBusy(true);
    setFeedback(null);
    try {
      await applyOutcome(await purchaseProAnnual(), 'pro', {
        confirme: t('paywall.activated'),
        neutre: t('paywall.activationEnCours'),
      });
    } finally {
      if (monte.current) setBusy(false);
    }
  };

  // Restauration : on attend « au moins Accélérateur » — s'il n'y a rien à restaurer, le serveur
  // reste free et le message neutre ne promet rien.
  const restore = async () => {
    if (busy) return;
    setBusy(true);
    setFeedback(null);
    try {
      await applyOutcome(await restorePurchases(), 'plus', {
        confirme: t('paywall.restored'),
        neutre: t('paywall.restoreEnCours'),
      });
    } finally {
      if (monte.current) setBusy(false);
    }
  };

  const contactSchool = () => {
    ouvrirLien(
      `mailto:${SCHOOL_CONTACT_EMAIL}?subject=${encodeURIComponent(tBrut('paywall.schoolMailSubject'))}`,
    );
  };

  return (
    <SafeAreaView testID={testID} style={styles.container} edges={['top', 'bottom']}>
      <DecorBlob variant="paywall" />
      <View style={styles.header}>
        <Pressable testID="pw-close" accessibilityLabel={t('a11y.close')} onPress={onClose} hitSlop={12} style={styles.closeBtn}>
          <Ionicons name="close" size={22} color={colors.night} />
        </Pressable>
        <View style={styles.currentPlanRow}>
          <Text style={styles.currentPlan}>{t('paywall.currentPlan')}</Text>
          <GradientText testID="pw-current-plan" style={styles.currentPlanValue}>
            {PLAN_LABEL[currentPlan]}
          </GradientText>
        </View>
      </View>

      {reason ? (
        <View style={styles.motif} testID="pw-reason">
          <Text style={styles.motifTexte}>
            {t(reason === 'not_included' ? 'paywall.notIncluded' : 'paywall.capReached', {
              date: prochaineRemiseAZero(),
            })}
          </Text>
        </View>
      ) : null}

      <Text style={styles.title}>{t('paywall.title')}</Text>

      <View style={styles.tabs}>
        <Pressable
          testID="pw-tab-individuel"
          onPress={() => setTab('individuel')}
          style={[styles.tab, tab === 'individuel' && styles.tabActive]}
        >
          <Text style={[styles.tabText, tab === 'individuel' && styles.tabTextActive]}>{t('paywall.tabIndividual')}</Text>
        </Pressable>
        <Pressable
          testID="pw-tab-ecoles"
          onPress={() => setTab('ecoles')}
          style={[styles.tab, tab === 'ecoles' && styles.tabActive]}
        >
          <Text style={[styles.tabText, tab === 'ecoles' && styles.tabTextActive]}>{t('paywall.tabSchools')}</Text>
        </Pressable>
      </View>

      <ScrollView contentContainerStyle={styles.content} showsVerticalScrollIndicator={false}>
        {tab === 'individuel' ? (
          <>
            {individualPlans(tarifs.mensuels).map((plan) => (
              <PlanCard
                key={plan.key}
                plan={plan}
                isCurrent={plan.key === currentPlan}
                isRecommended={plan.key === recommended}
                selected={plan.key === selected}
                canPurchase={BILLING_AVAILABLE && achatPossible(currentPlan, plan.key)}
                onSelect={() => setSelected(plan.key)}
                onSubscribe={() => void subscribe(plan.key)}
              />
            ))}

            {/* Sous les plans, jamais à leur place : c'est une autre façon de payer le Pro, pas un
                quatrième plan. Absente, elle ne laisse aucune trace dans la mise en page. */}
            {tarifs.annuelle && BILLING_AVAILABLE && achatPossible(currentPlan, 'pro') ? (
              <AnnualOfferCard offre={tarifs.annuelle} onSubscribe={() => void subscribeAnnual()} busy={busy} />
            ) : null}

            {feedback ? (
              <Text testID="pw-message" style={[styles.message, styles[`message_${feedback.tone}`]]}>
                {feedback.text}
              </Text>
            ) : null}

            {/* Restauration des achats : obligatoire Apple. Masquée si le store n'est pas branché. */}
            {BILLING_AVAILABLE ? (
              <Pressable
                testID="pw-restore"
                accessibilityRole="button"
                disabled={busy}
                onPress={() => void restore()}
                style={styles.restore}
              >
                <Text style={styles.restoreText}>{t('paywall.restore')}</Text>
              </Pressable>
            ) : (
              <Text testID="pw-unavailable" style={styles.unavailable}>
                {t('paywall.bientotSurPlateforme')}
              </Text>
            )}

            {/* Règle 3.1.2 d'Apple : renouvellement, résiliation, et liens vers les documents juridiques.
                Les liens ouvrent les écrans de l'app par-dessus le paywall, jamais le site (règle 11). */}
            <View style={styles.mentions} testID="pw-mentions">
              <Text style={styles.mentionTexte}>{t('paywall.mentionAbonnement')}</Text>
              <View style={styles.liensLegaux}>
                <Pressable accessibilityRole="link" onPress={() => setDocJuridique('cgu')} hitSlop={8} testID="pw-lien-cgu">
                  <Text style={styles.lienLegal}>{t('acceptTerms.docTerms')}</Text>
                </Pressable>
                <Text style={styles.mentionTexte}>·</Text>
                <Pressable accessibilityRole="link" onPress={() => setDocJuridique('confidentialite')} hitSlop={8} testID="pw-lien-confidentialite">
                  <Text style={styles.lienLegal}>{t('acceptTerms.docPrivacy')}</Text>
                </Pressable>
              </View>
              {horsFrancais() ? (
                <Text style={styles.mentionTexte} testID="pw-documents-fr">
                  {t('legalHeaders.documentsEnFrancais')}
                </Text>
              ) : null}
            </View>
          </>
        ) : (
          <View testID="pw-school-card" style={styles.schoolCard}>
            <Text style={styles.planName}>{schoolPlan().name}</Text>
            <FeatureList features={schoolPlan().features} />
            <GradientButton label={t('paywall.contact')} onPress={contactSchool} testID="pw-contact" />
          </View>
        )}
      </ScrollView>

      <Modal visible={docJuridique !== null} animationType="slide" onRequestClose={() => setDocJuridique(null)}>
        {docJuridique === 'cgu' ? (
          <TermsScreen onBack={() => setDocJuridique(null)} onOpenPrivacy={() => setDocJuridique('confidentialite')} />
        ) : docJuridique === 'confidentialite' ? (
          <PrivacyScreen onBack={() => setDocJuridique(null)} onOpenTerms={() => setDocJuridique('cgu')} />
        ) : null}
      </Modal>
    </SafeAreaView>
  );
}

function PlanCard({
  plan,
  isCurrent,
  isRecommended,
  selected,
  canPurchase,
  onSelect,
  onSubscribe,
}: {
  plan: PlanCardData;
  isCurrent: boolean;
  isRecommended: boolean;
  selected: boolean;
  canPurchase: boolean;
  onSelect: () => void;
  onSubscribe: () => void;
}) {
  const body = (
    <>
      <View style={styles.badges}>
        {plan.popular ? (
          <LinearGradient
            colors={gradients.brand}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 0 }}
            style={styles.badgePopular}
          >
            <Ionicons name="star" size={12} color={colors.white} />
            <Text testID={`pw-badge-${plan.key}`} style={styles.badgePopularText}>
              {t('paywall.recommended')}
            </Text>
          </LinearGradient>
        ) : null}
        {/* Sur la carte déjà marquée « Recommandé », un second bandeau répéterait le premier. */}
        {isRecommended && !plan.popular ? (
          <Text testID={`pw-reco-${plan.key}`} style={styles.badgeReco}>
            Recommandé pour toi
          </Text>
        ) : null}
      </View>

      <Text style={styles.planName}>{plan.name}</Text>
      <Text style={styles.price}>
        {plan.price} <Text style={styles.period}>{plan.period}</Text>
      </Text>
      {plan.ecart ? (
        <Text testID={`pw-ecart-${plan.key}`} style={styles.ecart}>
          {plan.ecart}
        </Text>
      ) : null}
      <Text style={styles.tagline}>{plan.tagline}</Text>

      <FeatureList features={plan.features} />

      {isCurrent ? (
        <Text testID={`pw-current-${plan.key}`} style={styles.current}>
          {t('paywall.tonPlan')}
        </Text>
      ) : plan.key !== 'free' && canPurchase && plan.prixFournisseur === false ? (
        // RÈGLE 12 : pas de bouton d'achat à côté d'un prix qui ne vient pas du fournisseur. Le
        // repli du dépôt peut différer de ce que le store débitera.
        <Text testID={`pw-prix-indispo-${plan.key}`} style={styles.current}>
          {t('paywall.prixIndisponibles')}
        </Text>
      ) : plan.key !== 'free' && canPurchase ? (
        // Même libellé partout ; le plan mis en avant garde un CTA dégradé (style seulement).
        plan.popular ? (
          <GradientButton label={t('paywall.subscribe')} onPress={onSubscribe} testID={`pw-subscribe-${plan.key}`} />
        ) : (
          <Button title={t('paywall.subscribe')} onPress={onSubscribe} testID={`pw-subscribe-${plan.key}`} />
        )
      ) : null}
    </>
  );

  // Carte sélectionnée (celle sur laquelle on appuie) → contour dégradé charte ; sinon carte blanche simple.
  if (selected) {
    return (
      <Pressable testID={`pw-plan-${plan.key}`} accessibilityRole="button" accessibilityState={{ selected }} onPress={onSelect}>
        <LinearGradient colors={gradients.brand} start={{ x: 0, y: 0 }} end={{ x: 1, y: 1 }} style={styles.cardBorder}>
          <View style={styles.cardInner}>{body}</View>
        </LinearGradient>
      </Pressable>
    );
  }

  return (
    <Pressable
      testID={`pw-plan-${plan.key}`}
      accessibilityRole="button"
      accessibilityState={{ selected }}
      onPress={onSelect}
      style={styles.cardPlain}
    >
      {body}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  // Encart de motif : même grammaire que les autres encarts d'information de l'app (fond teinté
  // clair, texte foncé, rayon de carte). Aucune valeur en dur.
  motif: {
    marginHorizontal: screenPadding,
    marginBottom: spacing.md,
    padding: spacing.md,
    borderRadius: radius.md,
    backgroundColor: colors.blueSoft,
  },
  motifTexte: {
    fontSize: typography.small.size,
    lineHeight: typography.small.lineHeight,
    color: colors.grey900,
  },
  container: { flex: 1, backgroundColor: colors.grey50, paddingHorizontal: screenPadding },
  header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingTop: spacing.lg },
  closeBtn: {
    width: 44,
    height: 44,
    borderRadius: radius.pill,
    backgroundColor: colors.white,
    alignItems: 'center',
    justifyContent: 'center',
    ...shadow.card,
  },
  currentPlanRow: { flexDirection: 'row', alignItems: 'center' },
  currentPlan: { color: colors.grey500, fontSize: typography.small.size },
  currentPlanValue: { fontSize: typography.small.size, fontWeight: '700' },
  title: {
    color: colors.night,
    fontSize: typography.h1.size,
    fontWeight: typography.h1.weight,
    marginTop: spacing.md,
  },
  tabs: { flexDirection: 'row', gap: spacing.xs, marginTop: spacing.lg, padding: spacing.xs / 2, borderRadius: radius.pill, backgroundColor: colors.grey100 },
  tab: { flex: 1, paddingVertical: spacing.sm, borderRadius: radius.pill, alignItems: 'center' },
  tabActive: { backgroundColor: colors.white, ...shadow.card },
  tabText: { color: colors.grey500, fontWeight: typography.bodyMd.weight },
  tabTextActive: { color: colors.night, fontWeight: '700' },
  content: { gap: spacing.md, paddingVertical: spacing.lg },

  // Cartes plans (thème clair)
  cardPlain: {
    borderRadius: radius.xl,
    padding: spacing.lg,
    gap: spacing.xs,
    backgroundColor: colors.white,
    borderWidth: 1.5,
    borderColor: colors.grey200,
    ...shadow.card,
  },
  cardBorder: { borderRadius: radius.xl, padding: 1.5, ...shadow.card },
  cardInner: { borderRadius: radius.xl, padding: spacing.lg, gap: spacing.xs, backgroundColor: colors.white },

  badges: { flexDirection: 'row', gap: spacing.sm, flexWrap: 'wrap', alignItems: 'center', minHeight: 4 },
  badgePopular: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingHorizontal: spacing.sm,
    paddingVertical: 3,
    borderRadius: radius.pill,
  },
  badgePopularText: { color: colors.white, fontSize: typography.caption.size, fontWeight: '700' },
  badgeReco: {
    color: colors.electric,
    backgroundColor: colors.blueSoft,
    fontSize: typography.caption.size,
    fontWeight: '700',
    paddingHorizontal: spacing.sm,
    paddingVertical: 2,
    borderRadius: radius.sm,
    overflow: 'hidden',
  },
  planName: { fontSize: typography.h2.size, fontWeight: '800', color: colors.night },
  price: { fontSize: typography.h2.size, fontWeight: typography.h2.weight, color: colors.night },
  period: { fontSize: typography.small.size, fontWeight: typography.body.weight, color: colors.grey500 },
  tagline: { fontSize: typography.body.size, color: colors.grey500, marginBottom: spacing.xs },
  ecart: { fontSize: typography.small.size, fontWeight: typography.bodyMd.weight, color: colors.violet, marginBottom: spacing.xs },
  features: { gap: spacing.sm, marginVertical: spacing.xs },
  featureRow: { flexDirection: 'row', alignItems: 'flex-start', gap: spacing.sm },
  check: { marginTop: 1 },
  featureText: { flex: 1, fontSize: typography.small.size, lineHeight: typography.small.size + 6, color: colors.grey700 },
  current: { marginTop: spacing.sm, color: colors.grey500, fontWeight: typography.bodyMd.weight, textAlign: 'center' },
  message: { fontSize: typography.small.size, fontWeight: '600', textAlign: 'center', marginTop: spacing.xs },
  message_success: { color: colors.success },
  message_info: { color: colors.warning },
  message_error: { color: colors.error },
  restore: { alignSelf: 'center', padding: spacing.sm, marginTop: spacing.xs },
  mentions: { gap: spacing.xs, marginTop: spacing.sm, alignItems: 'center' },
  mentionTexte: { fontSize: typography.small.size, lineHeight: typography.small.lineHeight, color: colors.grey500, textAlign: 'center' },
  liensLegaux: { flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', gap: spacing.sm },
  lienLegal: { fontSize: typography.small.size, lineHeight: typography.small.lineHeight, color: colors.grey700, fontWeight: '600', textDecorationLine: 'underline' },
  restoreText: { fontSize: typography.small.size, color: colors.grey500, fontWeight: '600', textDecorationLine: 'underline' },
  unavailable: { alignSelf: 'center', marginTop: spacing.xs, fontSize: typography.small.size, color: colors.grey500, textAlign: 'center' },
  schoolCard: { backgroundColor: colors.white, borderRadius: radius.xl, padding: spacing.lg, gap: spacing.xs, borderWidth: 1, borderColor: colors.grey200, ...shadow.card },
});
